import classNames from "classnames";
import * as _ from "lodash";
import React, { ChangeEvent } from "react";
import {
  Component,
  Position,
  ProgressBar,
  PuzzleButton,
  PuzzleCheckbox
} from "puzzle";
import { FILE, FileCount, Files, DROPUPLOAD_PROPS, STATE, REJECTED_FILES_DATA } from "./Types"
import { ANNOUNCEMENTS_TYPE } from "../../util/Util";
import { Container } from "puzzle";
import { CheckboxChangeEvent } from "@progress/kendo-react-inputs";
import DragAndDropWrapper from "../../UI/DragAndDrop/DragAndDropWrapper";
import DragAndDropPlaceholder from "../../UI/DragAndDrop/DragAndDropPlaceholder";

const initialState: STATE = {
  allowUpload: false,
  deletedFiles: [],
  dragging: false,
  droppedFiles: [],
  extractArchive: false,
  extractArchiveEnabled: false,
  failed: 0,
  progresses: {},
  projectId: 0,
  submissionFolder: '',
  uploaded: 0,
  uploadFiles: [],
  zipUploadInProgress: false
};

class DropUpload<P = {}, S extends Partial<STATE> = {}> extends Component<DROPUPLOAD_PROPS & P, STATE & S> {

  private finallyUploadProgresses: any;

  protected static UploadStatus = {
    UPLOADED: 'uploaded',
    REQUESTED: 'requested',
    NOT_PROCESSED: 'notProcessed',
    FAILED: 'failed',
    ALREADY_UPLOADED: 'alreadyUploaded'
  };


  filesInput = React.createRef<HTMLInputElement>();
  dropUploadDomElement = React.createRef<HTMLDivElement>();

  public state: STATE = initialState;

  componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<any>, snapshot?: any): void {
    const propsChanged = !_.isEqual(prevProps, this.props);
    const stateChanged = !_.isEqual(prevState, this.state);

    if (propsChanged || stateChanged) {
      this.props.onChange && this.props.onChange(this.state.droppedFiles, this.state.deletedFiles);
    }

    if (this.finallyUploadProgresses) {
      this.dispatch('updateProgressesToUnzippedFiles', Position.end, {
        dispatcherId: 'AsyncUploadDispatcher',
        progresses: this.finallyUploadProgresses
      });
      this.finallyUploadProgresses = null;
    }
  }

  moduleDidMount = () => {
    this.enableUpload(this.props.projectId || 0);

    this.childModuleDidMount();
  };

  /**
   * Child function for moduleDidMount
   * Ready for overriding in child classes
   */
  childModuleDidMount = () => {
  }

  /**
   * Receives files form drop or browse event's and starts upload process if all conditions for starting download
   * satisfied, else raise warning
   * @param files
   */
  beforeUploadStart = (files: any) => {
    const {
      allowUpload,
      zipUploadInProgress
    } = this.state;

    if (!zipUploadInProgress) {
      if (allowUpload) {
        if (files.length) {
          this.startFileUpload(files, 0);
        }
      } else {
        this.setState({ dragging: false }, () => {
          this.dispatch('uploadWarning');
        });
      }
    } else {
      this.setState({
        dragging: false
      });
    }

    if (this.filesInput.current) {
      this.filesInput.current.value = '';
    }
  };

  /**
   * Changes dom so screen reader could read changes after files were added or deleted.
   * @param announcementType
   * @param updatedFiles
   */
  setAnnouncements = (announcementType: string, updatedFiles: FILE[]) => {
    if (updatedFiles?.length) {
      let el = this.dropUploadDomElement.current;
      if (el) {
        let alertDiv: HTMLDivElement | null = el.querySelector('.accessibilityAnnouncements');
        if (alertDiv) {
          let text: string = updatedFiles.reduce((array: string[], file: FILE) => {
            array.push(file.fileName);
            return array
          }, []).join(' ');

          switch (announcementType) {
            case ANNOUNCEMENTS_TYPE.UPLOAD :
              text = this.translate("uploadAnnouncement", { fileNames: text });
              break;
            case ANNOUNCEMENTS_TYPE.DELETE :
              text = this.translate("deleteAnnouncement", { fileNames: text });
              break;
            default:
              text = '';
              break;
          }
          alertDiv.textContent = text;
        }
      }

      setTimeout(this.removeAnnouncement, 1000);
    }
  };

  /**
   * Creates array of files that are already contained in submission
   * @param data
   */
  createAlreadyUploadedData(data: any = []): FILE[] {
    return data.map((item: any) => ({
      fileName: item.documentName || item.fileName,
      uploaded: DropUpload.UploadStatus.ALREADY_UPLOADED,
      documentId: item.documentId,
      targetId: item.targetId,
      repositoryTicket: item.repositoryTicket
    }));
  };

  /**
   *  If item is selected put it into array else returns item
   * @param array
   * @param item
   */
  collocateItem = (array: any[], item: any) => {
    const { selectionField } = this.props;

    if (!item[selectionField]) return item;
    array.push({
      ...item,
      [selectionField]: false
    });
  };

  /**
   * Removes files form current batch
   */
  deleteMultipleFiles = () => {
    let { droppedFiles } = this.state;
    let removedItems: any = [];
    droppedFiles.filter(this.collocateItem.bind(this, removedItems));
    if (removedItems.length) {
      this.removeFile(removedItems);
    }
  };

  /**
   * Returns list of all files that are currently being uploaded
   */
  createUploadedFilesList = () => {
    const { droppedFiles, dragging } = this.state;
    let items = droppedFiles.map(this.createUploadedFileListItem);
    const classes = classNames("tp-uploaded-list", {
      "tp-masked": dragging
    });
    return (
      <div className={classes}>
        {items}
      </div>
    );
  };
  /**
   * Returns currently uploading file
   * @param: file
   * @index: number
   */
  createUploadedFileListItem = (file: FILE, index: number) => {
    const { progresses = {} } = this.state;
    let { moduleId } = this.props;
    const uploadGroupId = moduleId;
    const itemClassName = classNames('tp-uploaded-list-item ', {
      'tp-uploaded-list-item-uploading': file.uploaded !== 'alreadyUploaded' && !file.repositoryTicket
    });
    return (
      <div key={"itemName" + file.fileName + index}
           className={itemClassName}>
        <div className="tp-uploaded-list-item-name k-text-ellipsis">
          {file.fileName}
        </div>
        <div className="tp-uploaded-list-item-extension k-text-ellipsis">
          {file.fileName.split(".")[1]}
        </div>
        <ProgressBar
          key={`ProgressBar_${uploadGroupId}_${file.fileName}`}
          className={'tp-uploaded-list-item-status k-text-ellipsis'}
          file={file}
          fileName={file.fileName}
          group={uploadGroupId}
          progresses={progresses}
          cancelable={true}
          onCancel={this.removeFile.bind(this, [file])}
        />
      </div>
    )
  };

  /**
   * Creates drag and drop placeholder
   */
  createUploadPlaceholder = () => {
    const {
      extractArchiveEnabled,
      dragging,
      extractArchive
    } = this.state;

    return (
      <DragAndDropPlaceholder
        dragging={dragging}
        dNdPlaceholderTitle={this.translate('dragAndDropTitle')}
        buttonDisabled={!this.state.allowUpload}
        buttonOnClick={this.fileUploadHandler}
        buttonText={this.translate('selectFilesBtn')}
        extractArchiveEnabled={extractArchiveEnabled}
        checkboxValue={extractArchive}
        checkboxOnChange={(event: CheckboxChangeEvent) => {
          this.setState({ extractArchive: event.value })
        }}
        checkboxDisabled={!this.state.allowUpload}
        checkboxLabel={this.translate('extractArchive')}
      />
    );
  };


  /**
   * Renderer for upload status section
   */
  createUploadStatusSection = () => {
    const { droppedFiles, uploaded } = this.state;

    return (
      <div className={'tp-files-uploaded-info-bar'}>
        {!!droppedFiles.length ?
          <>
            <div className={'tp-files-counter'}>
              <div className="tp-icon tp-icon-sm tp-check-circle" />
              {this.translate('filesFound', { numberOfFiles: uploaded })}
            </div>
            <div className=''>
              <PuzzleButton
                className={'tp-delete-files'}
                onClick={this.deleteMultipleFiles}
                iconClass={'tp-icon tp-delete'}
                themeColor={'primary'}
                fillMode="flat"
              >
                {this.translate('deleteFiles')}
              </PuzzleButton>
              <PuzzleButton
                iconClass={'tp-icon tp-add-circle'}
                fillMode='flat'
                themeColor={'primary'}
                onClick={this.fileUploadHandler}
              >
                {this.translate('addMoreFiles')}
              </PuzzleButton>
            </div>
          </>
          : null
        }
      </div>
    );
  };

  /**
   * Drag leave event handler
   * @param event
   */
  dragOverHandler = (event: React.DragEvent) => {
    if (!this.state.dragging) {
      this.setState({
        dragging: true
      });
    }
  };

  /**
   * Drag leave event handler
   * @param event
   */
  dragLeaveHandler = (event: React.DragEvent) => {
    if (this.state.dragging) {
      this.setState({
        dragging: false
      });
    }
  };

  /**
   * Handles response received upon uploading edit's data toward response.
   */
  dropUploadStatus = (uploadStatus: any, sentRequest: any, multiErrors: any) => {
    if (uploadStatus.uploadGroupId === this.moduleId) {
      const response = uploadStatus;
      const { uploaded, failed } = response.data.reduce((data: Files, item: any) => {
        const { uploadedFiles, rejectedFiles } = item;
        if (uploadedFiles) data.uploaded.push(...uploadedFiles);
        if (rejectedFiles) data.failed.push(...rejectedFiles);
        return data;
      }, {
        uploaded: [],
        failed: []
      });

      const { droppedFiles, submissionFolder } = this.state;
      let files = [...droppedFiles];
      let folder = submissionFolder || response.submissionFolder;
      let index: number;
      const filesLastIndex: number = files.length - 1;

      let unzippedArchives = [];
      let requestedFile = sentRequest?.file;
      let extractRequested = sentRequest?.extractArchive;

      if (extractRequested && requestedFile?.length) {
        unzippedArchives = requestedFile.reduce((archives: any, file: any) => {
          if (file.type.includes('zip')) archives.push(Object.assign(file, { fileName: file.name }));
          return archives
        }, []);
        unzippedArchives.forEach((archive: string) => {
          index = _.findLastIndex(files, this.findMatchingFileByName.bind(this, archive));
          if (index !== -1) files.splice(index, 1);
        });
      }

      files = this.mapUploadedFiles(uploaded, files, filesLastIndex, extractRequested);

      files = this.mapFailedUploadedFiles(failed, files, filesLastIndex, extractRequested);

      if (!_.isEmpty(multiErrors)) {
        multiErrors.forEach((errorItem: any) => {
          files = files.filter((item: any) => {
            return errorItem.fileName !== item.fileName;
          })
        })
      }

      this.updateStateAndContinueUpload(files, folder, false, uploaded);
    }
  };

  /**
   * Enables upload button and drag upload if criteria is satisfied
   */
  enableUpload = (projectId: number) => {
    const { isDisabled } = this.props;
    if (!isDisabled) {
      this.setState({
        allowUpload: !!projectId,
        projectId
      });
    }
  };

  /**
   * Map method for dropped and new files for update
   */
  findFilesForUpdate = (uploadFiles: any[], droppedFile: any) => {
    let findUploadedFile = uploadFiles.find((file: any) => {
      let status = droppedFile.uploaded !== DropUpload.UploadStatus.FAILED && droppedFile.uploaded !== DropUpload.UploadStatus.ALREADY_UPLOADED;
      return status && !droppedFile.repositoryTicket && droppedFile.fileName === file.name;
    });
    if (findUploadedFile) {
      droppedFile = {
        fileName: findUploadedFile.name,
        uploaded: DropUpload.UploadStatus.UPLOADED
      }
    }

    return droppedFile;
  };

  /**
   * Return true if current fileName matches uploaded file name
   */
  findMatchingFileByName(uploadedFile: any, item: any): boolean {
    let ticketMatch = !item.repositoryTicket || item.repositoryTicket === uploadedFile.repositoryTicket;
    return item.fileName === uploadedFile.fileName && ticketMatch && item.uploaded !== DropUpload.UploadStatus.ALREADY_UPLOADED;
  }

  /**
   * Triggers click event on file input field
   */
  fileUploadHandler = () => {
    this.filesInput.current?.click();
  };

  /**
   * Handles upload of files selected through the file input.
   * @param e
   */
  fileSelectHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const { files } = e.currentTarget;
    this.beforeUploadStart(files);
  };

  /**
   * Returns failed files
   */
  getFailedFiles = () => {
    return DropUpload.UploadStatus.FAILED;
  };

  /**
   * Gets list of files within deletedFiles that have ALREADY_UPLOADED status.
   * ALREADY_UPLOADED status means that files are on submission, not in tempFolder.
   */
  getFilesRemovedFromSubmission = () => {
    const files = this.state.deletedFiles.filter((item: any) => item.uploaded === DropUpload.UploadStatus.ALREADY_UPLOADED);
    return files;
  };

  /**
   * Gets submission folder.
   */
  getSubmissionFolder = () => {
    return this.state.submissionFolder;
  };

  /**
   * Get submissionId for upload params
   */
  getSubmissionIdForUpload = () => {
    const { submissionId, submissionStatus } = this.props;
    return submissionId && submissionStatus ? { submissionId } : null;
  }

  /**
   * Drag over handler
   */
  maskedDragOverHandler = (event: React.DragEvent) => {
    event.preventDefault();
  };

  /**
   * Map failed uploaded files from ajax response
   */
  mapFailedUploadedFiles = (failed: FILE[], files: FILE[], filesLastIndex: number, extractRequested: boolean) => {
    const failedLength = failed.length;
    if (failedLength) {
      let index;
      for (let i = 0; i < failedLength; i++) {
        index = _.findLastIndex(files, this.findMatchingFileByName.bind(this, failed[i]), filesLastIndex);
        if (index > -1) {
          files[index].uploaded = DropUpload.UploadStatus.FAILED;
          files[index].repositoryTicket = failed[i].repositoryTicket;
          files[index].fileFormatIds = failed[i].fileFormatIds;
          files[index].rejectionCode = failed[i].rejectionCode;
        } else if (extractRequested) {
          files.push({
            fileName: failed[i].fileName,
            uploaded: DropUpload.UploadStatus.FAILED,
            repositoryTicket: failed[i].repositoryTicket,
            fileFormatIds: failed[i].fileFormatIds,
            rejectionCode: failed[i].rejectionCode
          })
        }
      }
    }

    return files;
  };
  /**
   * Forms rejection message based on errorType.
   * @param files
   * @param errorType
   * @param hasSuggestion
   * @param additionalText
   */
  private getRejectionMessage = (files: any[], errorType: string, hasSuggestion: boolean = false, additionalText?: string): string => {
    let message: string = '';
    if (files.length) {
      message += '{{brTag}}{{sBold}}';
      message += this.translate(`rejectedFiles.${errorType}`);
      message += '{{eBold}}';
      if (hasSuggestion && additionalText) {
        message += (' ' + this.translate(`rejectedFiles.${additionalText}`));
      }
      message += '{{brTag}}';
      message += this.translate('rejectedFiles.files', {
        files: files.join(', ')
      });
      message += '{{brTag}}';
      if (hasSuggestion) {
        message += this.translate(`rejectedFiles.${errorType}Suggestion`);
        message += '{{brTag}}';
      }
    }
    return message;
  };

  removeFailedFilesFromGrid = () => {
    const uploadedFiles: any [] = (this.state.droppedFiles || []).filter((file: any) => file.uploaded !== DropUpload.UploadStatus.FAILED);
    this.setState({
      droppedFiles: uploadedFiles
    });
  };

  /**
   * Structures data properly per rejectionCode to be shown od Rejected files warning.
   * @param group
   */
  handleRejectionCode = (group: { rejected: any[], uploaded: any[] }): REJECTED_FILES_DATA => {
    const types: REJECTED_FILES_DATA = (group.rejected || []).reduce((types, file) => {
      switch (file.rejectionCode) {
        case 0:
          types.duplicated.push(file.fileName);
          break;
        case 1:
          types.invalidFileName.push(file.fileName);
          break;
        default:
          types.fileNameTooLong.push(file.fileName);
          break;
      }
      return types;
    }, {
      duplicated: [],
      invalidFileName: [],
      fileNameTooLong: []
    });

    return types;
  };
  /**
   * Map uploaded files from ajax response
   */
  mapUploadedFiles = (uploaded: FILE[], files: FILE[], filesLastIndex: number, extractRequested: boolean) => {
    const uploadedLength = uploaded.length;
    let progresses = {};
    if (uploadedLength) {
      let index;
      for (let i = 0; i < uploadedLength; i++) {
        index = _.findLastIndex(files, this.findMatchingFileByName.bind(this, uploaded[i]), filesLastIndex);
        if (index > -1) {
          files[index].uploaded = DropUpload.UploadStatus.UPLOADED;
          files[index].repositoryTicket = uploaded[i].repositoryTicket;
          files[index].fileFormatIds = uploaded[i].fileFormatIds;
        } else if (extractRequested) {
          Object.assign(progresses, { [uploaded[i].fileName]: 100 });
          files.push({
            fileName: uploaded[i].fileName,
            uploaded: DropUpload.UploadStatus.UPLOADED,
            repositoryTicket: uploaded[i].repositoryTicket,
            fileFormatIds: uploaded[i].fileFormatIds,
            isAlreadyUpload: true
          })
        }
      }
      if (extractRequested && !_.isEmpty(progresses)) {
        this.finallyUploadProgresses = progresses;
      }
    }

    return files;
  };

  /**
   * Drop event handler
   * @param e
   */
  onDropHandler = (e: React.DragEvent) => {
    e.preventDefault();
    const { files, items } = e.dataTransfer;
    const directoryExists = [...items].some((fileObject: any) => fileObject.webkitGetAsEntry()?.isDirectory);
    if (directoryExists || !items[0].webkitGetAsEntry()) {//second condition is to cover PDII-33204
      this.setState({ dragging: false }, () => {
        this.dispatch('invalidDropWarning');
      });
    } else {
      this.beforeUploadStart(files);
    }
  };

  /**
   * Returns files ticket if it's temporaryFile.
   * @param array
   * @param item
   */
  getRemovedFilesTickets = (array: string[], item: FILE) => {
    if (item.repositoryTicket && item.uploaded === 'uploaded') {
      array.push(item.repositoryTicket);
    }
    return array;
  };

  /**
   * Dropped Files getter
   */
  getFiles = () => {
    return _.cloneDeep(this.state.droppedFiles)
  };

  /**
   * Returns array if file tickets of all deletedFiles
   */
  getDeletedFilesTickets = () => {
    const removeFileParams = {
      tickets: this.state.deletedFiles.reduce(this.getRemovedFilesTickets, [])
    };
    return removeFileParams;
  };
  /**
   * Creates proper format for copy files warning msg header
   * @param duplicates
   */
  createMoveFilesMessageHeader = (duplicates: any[]) => {
    let items = duplicates.map((file: any) => file.fileName);
    return items.join('<br>');
  };

  /**
   * Remove file and delete it from repo if is needed
   */
  removeFile = (files: FILE[]): void => {
    const removeFileParams = files.reduce(this.getRemovedFilesTickets, []);
    if (removeFileParams.length) {
      if (this.props.hasFileConnection) {
        this.dispatch('removeFilesWithRestriction', Position.end, {
          moduleId: this.moduleId,
          removeFileParams: {
            tickets: removeFileParams
          }
        });
      } else {
        if (!this.props.deleteOnSave) {
          this.dispatch('removeTempFile', '', {
            removeFileParams: {
              tickets: removeFileParams
            }
          });
        }
      }
    }

    let droppedFiles = [...this.state.droppedFiles];
    let length = droppedFiles.length;
    let filesLength = files.length;
    let findDeleted = [];

    while (length--) {
      let counter = 0;
      while (filesLength > counter) {
        if (droppedFiles[length].fileName === files[counter].fileName && files[counter].repositoryTicket === droppedFiles[length].repositoryTicket) {
          findDeleted.push(droppedFiles[length]);
          droppedFiles.splice(length, 1);
          break;
        }
        counter++;
      }
    }

    const { uploaded, failed } = this.totalUploaded(droppedFiles);
    let deletedFiles = [...this.state.deletedFiles];
    deletedFiles = findDeleted.length ? deletedFiles.concat(findDeleted) : deletedFiles;
    this.setState({
      droppedFiles,
      uploaded,
      failed,
      deletedFiles
    }, () => {
      _.isEmpty(droppedFiles) && this.dispatch('enableProject');
      this.setAnnouncements(ANNOUNCEMENTS_TYPE.DELETE, files);
    });
  };

  /**
   * Render checkbox for extract zip option
   */
  renderExtractCheckBox = () => {
    const { extractArchive } = this.state;

    return (
      <Container className="tp-extract-checkbox-warp">
        <PuzzleCheckbox
          label={this.translate('extractArchive')}
          value={extractArchive}
          disabled={!this.state.allowUpload}
          onChange={(event: CheckboxChangeEvent) => {
            this.setState({ extractArchive: event.value })
          }}
        />
      </Container>
    );
  };

  /**
   * Reset to initial values
   */
  reset = () => this.setState(initialState);

  /**
   * Saves initial submissionFolder path.
   * @param submissionFolder
   */
  saveSubmissionFolder = (submissionFolder: string) => {
    if (submissionFolder) {
      this.setState({ submissionFolder });
    }
  };

  /**
   * Used for setting initial state when there are already existing files (Edit Submission)
   *
   * additionalData are used in some classes that extend DropUpload (e.g. ReferenceFiles)
   */
  setExistingFiles = (files: any, details: any, additionalData?: any): void => {
    const { project } = details;
    const existingFiles = files ? this.createAlreadyUploadedData(files) : [];

    this.setState({
      allowUpload: !!project,
      projectId: project,
      droppedFiles: existingFiles,
      dragging: false,
      uploaded: existingFiles.length,
    });
  };

  /**
   * Starts selected files upload
   * @param files
   * @param projectIdFromTemplate in case file is dropped to main grid, project ID is not set to the submission project field yet, so we have to pass it through this prop
   */
  startFileUpload = (files: any, projectIdFromTemplate: number | undefined) => {
    let fileObjects: FILE[] = [];
    let { extractArchive, projectId, uploadFiles } = this.state;
    if (!projectId) {
      projectId = this.props.projectId;
    }
    if (projectIdFromTemplate) {
      projectId = projectIdFromTemplate;
    }
    let isZipUpload = false;

    for (let i = 0; i < files.length; i++) {
      if (!isZipUpload) {
        isZipUpload = files[i].type.includes('zip');
      }

      fileObjects.push({
        fileName: files[i].name,
        uploaded: DropUpload.UploadStatus.REQUESTED,
        repositoryTicket: '',
        fileFormatIds: []
      });
    }

    uploadFiles = uploadFiles.concat(Array.from(files));

    let uploadParams: any = {
      file: uploadFiles,
      projectId,
      extractArchive,
      submissionFolder: this.getSubmissionFolder()
    };

    let submissionId = this.getSubmissionIdForUpload();
    Object.assign(uploadParams, submissionId);
    let requestParams = {
      dropUploadModuleId: this.moduleId,
      projectIdFromTemplate,
      uploadParams,
      uploadFiles,
      uploadGroupId: this.moduleId
    };

    this.setState({
      droppedFiles: this.state.droppedFiles.concat(fileObjects),
      dragging: false,
      zipUploadInProgress: isZipUpload
    }, () => {
      this.dispatch(this.props.uploadActionName, {
        dispatcherId: 'AsyncUploadDispatcher',
        pos: Position.end
      }, requestParams);
    });
  };

  setUploadProgress(progresses: any) {
    this.setState({
      progresses: progresses[this.moduleId] || {}
    });
  }

  /**
   * Counts the number of files that have been successfully uploaded and those that were not.
   */
  totalUploaded = (droppedFiles: FILE[]): FileCount => {
    const uploadedStatuses = [DropUpload.UploadStatus.UPLOADED, DropUpload.UploadStatus.ALREADY_UPLOADED];
    return droppedFiles.reduce((data: FileCount, file: FILE) => {
      if (uploadedStatuses.includes(file.uploaded)) {
        data.uploaded++;
      } else if (file.uploaded === DropUpload.UploadStatus.FAILED) {
        data.failed++;
      }
      return data;
    }, {
      uploaded: 0,
      failed: 0
    });
  };

  /**
   * Update files when upload is started
   */
  updateUpdatedFiles = (uploadFiles: any[]) => {
    let { droppedFiles } = this.state;

    droppedFiles = droppedFiles.map(this.findFilesForUpdate.bind(this, uploadFiles));

    this.setState({
      droppedFiles: droppedFiles
    });
  };

  /**
   * Updates data about uploading files, upon setting state calls next upload
   * @param updatedFiles
   * @param folder
   * @param forceAdd
   * @param lastUploaded
   */
  updateStateAndContinueUpload = (updatedFiles: FILE[], folder: string, forceAdd: boolean, lastUploaded: FILE[]) => {
    const { failed, uploaded } = this.totalUploaded(updatedFiles);
    this.setState({
      dragging: false,
      droppedFiles: updatedFiles,
      failed,
      submissionFolder: folder,
      uploaded,
      zipUploadInProgress: false
    }, () => {
      this.setAnnouncements(ANNOUNCEMENTS_TYPE.UPLOAD, lastUploaded);
    });
  };

  /**
   * Remove announcement from DOM
   */
  removeAnnouncement = () => {
    let el = this.isModuleMounted && this.dropUploadDomElement.current;
    if (el) {
      let alertDiv: HTMLDivElement | null = el.querySelector('.accessibilityAnnouncements');
      if (alertDiv) {
        alertDiv.textContent = '';
      }
    }
  };

  /**
   * Intercepts file's array and removes all copies (files with file names that are already uploaded)
   * Raise warning containing all files that were removed.
   *
   * @param files
   */
  removeDuplicateFiles = (files: any) => {
    const filesNames = this.state.droppedFiles.map((file: any) => file.fileName);
    const duplicates: string[] = [];
    const uniqueFiles: any[] = [];
    [...files].forEach((file: any) => {
      filesNames.includes(file.name) ? duplicates.push(file.name) : uniqueFiles.push(file);
    });

    if (duplicates.length) {
      this.dispatch('duplicateFilesWarning', Position.end, {
        files: duplicates.join('</br>')
      })
    }

    return uniqueFiles;
  };

  /**
   * Deleted files setter
   * @param files
   */
  setDeletedFiles = (files: FILE[]): void => {
    let { deletedFiles } = this.state;

    this.setState({
      deletedFiles: deletedFiles.concat(files)
    })
  };

  renderBody() {
    const {
      droppedFiles,
      zipUploadInProgress
    } = this.state;
    const { isDisabled, showUploadStatus = true, isCollapsed, submissionStarted, hideDialog } = this.props;
    const inputIdArray = [this.moduleName, this.moduleId, "SourceFiles"];
    if (hideDialog) {
      inputIdArray.push("hidden"); //For the a11y purposes. W/o this part there are 2 component w/ the same id
    }
    const inputId = inputIdArray.join("_");
    const dragAndDropWrapperClass = classNames(
      'tp-drag-and-drop-wrapper',
      {
        'k-display-none': hideDialog,
        'tp-collapsed': isCollapsed
      }
    );
    return (
      <Container
        ref={this.dropUploadDomElement}
        className={dragAndDropWrapperClass}
        flex1
      >
        <div
          role={'alert'}
          className={'sr-only accessibilityAnnouncements'}
        />
        <input
          disabled={isDisabled}
          type="file"
          ref={this.filesInput}
          className="tp-file-input"
          multiple
          id={inputId}
          onChange={this.fileSelectHandler}
        />
        <label htmlFor={inputId} className="sr-only">{this.translate("SRL-uploadLabel")}</label>
        <div onDragEnter={this.dragOverHandler} className="tp-drag-and-drop">
          {droppedFiles.length === 0 ? this.createUploadPlaceholder() : this.createUploadedFilesList()}
          <DragAndDropWrapper
            show={this.state.dragging && !zipUploadInProgress && !submissionStarted}
            dNdMessage={this.translate('dropFilesHere')}
            dragLeaveHandler={this.dragLeaveHandler}
            onDropHandler={this.onDropHandler}
            dragOverHandler={this.maskedDragOverHandler}
          />
        </div>

        {showUploadStatus && this.createUploadStatusSection()}
      </Container>
    );
  }
}

export default DropUpload;


